import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import config from 'config';
import { loadStripe, type Stripe as StripeJs } from '@stripe/stripe-js';
import {
  CheckoutElementsProvider,
  PaymentElement,
  useCheckoutElements,
} from '@stripe/react-stripe-js/checkout';
import type {
  CheckoutInterval,
  CheckoutPlan,
} from '@klab-berlin/api-sdk/lib/types/requests/Billing';
import service from '../../../services/billing.service';
import useRouter from '../../../utils/useRouter';
import useScreenSize from '../../../utils/useScreenSize';
import { customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import AppNavBar from '../../../components/AppLayout/MasterLayout/AppNavBar';
import StripeCheckoutSummary from '../../checkout/StripeCheckoutSummary/StripeCheckoutSummary';
import { PLAN_LABELS, REF_RABATT_CODE, VALID_PLANS } from '../../checkout/StripeCheckoutPage/checkout.constants';
import { selectUser } from '../../../reducers/user.selectors';
import { routes } from '../../../constants';

// SM-187: plans that carry the Ref-Rabatt (trainee) discount toggle on the
// Basic-upgrade payment page. Mirrors the pricing matrix — mF and KI (aiOnly)
// have no Ref-Rabatt.
const REF_RABATT_PLANS: readonly CheckoutPlan[] = ['pro', 'fullAccess'];

type CheckoutErrorKind =
  | 'InvalidPlan'
  | 'InvalidPromoCode'
  | 'CheckoutSessionFailed'
  | 'NetworkError';

interface ErrorData {
  code?: string;
  error?: string;
  reason?: string;
}

// Mirrors the kimuno service's `InvalidPromoCodeReason` enum. Used to pick the
// inline German message under the promo input so the user knows whether the
// code is just wrong, restricted to new customers, already redeemed, etc.
type PromoErrorReason =
  | 'unknown'
  | 'first-time-customer-only'
  | 'already-redeemed'
  | 'expired'
  | 'does-not-apply';

// English source strings double as i18next keys (the project's convention —
// see surrounding `t(...)` calls). Translation files override per locale; an
// untranslated key falls back to the English source rather than a dotted
// identifier leaking into the UI.
const PROMO_ERROR_MESSAGE_KEYS: Record<PromoErrorReason, string> = {
  'unknown': 'The code is invalid or does not exist — please try entering your code again',
  'first-time-customer-only': 'This promo code is only valid for new customers',
  'already-redeemed': 'This promo code has already been redeemed',
  'expired': 'This promo code is no longer active',
  'does-not-apply': 'This promo code does not apply to your selected plan',
};

function readPromoErrorReason(err: unknown): PromoErrorReason {
  const reason = (err as { data?: ErrorData })?.data?.reason;
  if (
    reason === 'first-time-customer-only' ||
    reason === 'already-redeemed' ||
    reason === 'expired' ||
    reason === 'does-not-apply'
  ) {
    return reason;
  }
  return 'unknown';
}

function isValidPlan(value: unknown): value is CheckoutPlan {
  return typeof value === 'string' && (VALID_PLANS as readonly string[]).includes(value);
}

function readQueryParam(
  query: Record<string, string | undefined>,
  key: string,
): string | undefined {
  const raw = query[key];
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
}

function mapErrorToKind(err: unknown): CheckoutErrorKind {
  const data = (err as { data?: ErrorData })?.data;
  const code = data?.code ?? data?.error;
  if (
    code === 'InvalidPlan' ||
    code === 'InvalidPromoCode' ||
    code === 'ResubscribeCheckoutSessionFailed'
  ) {
    return code === 'ResubscribeCheckoutSessionFailed' ? 'CheckoutSessionFailed' : code;
  }
  return 'NetworkError';
}

const ERROR_LABELS: Record<CheckoutErrorKind, string> = {
  InvalidPlan: 'The selected plan is not valid.',
  InvalidPromoCode: 'The entered promo code is not valid.',
  CheckoutSessionFailed: 'The payment could not be initiated. Please try again later.',
  NetworkError: 'A network error occurred. Please check your connection and try again.',
};

const ColumnContainer: React.FC<{
  dataTestId: string;
  bgcolor: string;
  isLargeScreen: boolean;
  children: React.ReactNode;
}> = ({ dataTestId, bgcolor, isLargeScreen, children }) => {
  const { paymentInformationContainer } = customSizes;
  return (
    <Box
      data-testid={dataTestId}
      flex={1}
      bgcolor={bgcolor}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        paddingY: isLargeScreen ? paymentInformationContainer.paddingY : spaces.xxl,
        paddingX: spaces.lg,
      }}
    >
      <Box
        sx={{
          width: isLargeScreen
            ? paymentInformationContainer.width.desktop
            : paymentInformationContainer.width.mobile,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

// Mirrors the registration-checkout's order-summary widget, trimmed to the
// resubscribe case: no trial branch (basis users never trial), no proration
// (no active subscription to prorate against). Reads the live Stripe order
// totals so the user sees the exact amount Stripe will charge today.
const OrderTotalSummary: React.FC = () => {
  const { t } = useTranslation();
  const checkoutState = useCheckoutElements();

  if (checkoutState.type !== 'success') return null;
  const { checkout } = checkoutState;
  const { total, recurring } = checkout;
  const hasDiscount = total.discount.minorUnitsAmount > 0;
  const isYearly = recurring?.interval === 'year';

  const intervalSuffix = isYearly
    ? t('{{amount}} per year', { amount: total.total.amount })
    : t('{{amount}} per month', { amount: total.total.amount });

  return (
    <Box data-testid='stripe-resubscribe-order-total'>
      {hasDiscount && (
        <Box sx={{ marginBottom: spaces.xs }}>
          <Typography
            variant='body2'
            sx={{ color: 'var(--color-text-secondary)', textDecoration: 'line-through' }}
            data-testid='stripe-resubscribe-order-standard-price'
          >
            {t('Standard price: {{amount}}', { amount: total.subtotal.amount })}
          </Typography>
          <Typography variant='body2' data-testid='stripe-resubscribe-order-discount'>
            {t('Discount: −{{amount}}', { amount: total.discount.amount })}
          </Typography>
        </Box>
      )}
      <Typography variant='subtitle1' sx={{ fontWeight: 600 }} data-testid='stripe-resubscribe-due-today'>
        {t('Total due today: {{amount}}', { amount: intervalSuffix })}
      </Typography>
    </Box>
  );
};

const CheckoutForm: React.FC<{
  onSubmitError: (kind: CheckoutErrorKind) => void;
}> = ({ onSubmitError }) => {
  const { t } = useTranslation();
  const checkoutState = useCheckoutElements();
  const [submitting, setSubmitting] = useState(false);

  const checkout = checkoutState.type === 'success' ? checkoutState.checkout : null;

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!checkout || submitting) return;

      setSubmitting(true);

      // `returnUrl` is intentionally omitted: the kimuno service stamps
      // `return_url` onto the Stripe Checkout Session at creation time
      // (`resolveResubscribeSuccessUrl()` → dashboard root). Passing it again
      // here would throw `IntegrationError: You cannot provide returnUrl …
      // when return_url was already provided when creating the Checkout
      // Session`, leaving the button stuck in its loading state.
      try {
        const result = await checkout.confirm({ redirect: 'always' });
        if (result.type === 'error') {
          onSubmitError(
            result.error.code === 'paymentFailed' ? 'CheckoutSessionFailed' : 'NetworkError'
          );
          setSubmitting(false);
        }
        // No-op on success — Stripe is doing a top-level redirect, the
        // component will unmount on its own.
      } catch (err) {
        // Defensive: any unexpected throw (integration mismatch, network
        // blip pre-redirect, etc.) must still release the button so the
        // user can retry instead of being stuck on a forever-spinner.
        // eslint-disable-next-line no-console
        onSubmitError('NetworkError');
        setSubmitting(false);
      }
    },
    [checkout, submitting, onSubmitError],
  );

  if (checkoutState.type === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '200px', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (checkoutState.type === 'error') {
    // Log the raw Stripe SDK detail for ops/Bugsnag rather than surfacing
    // it to the user — untranslated SDK strings leak internal Stripe copy
    // (e.g. "Your card was declined.") into a German UI. The alert renders
    // a translated fallback; the underlying error is still in the console
    // for a support case.
    // eslint-disable-next-line no-console
    console.error('[stripe-resubscribe] Elements error', checkoutState.error);
    return (
      <Alert severity='error' data-testid='stripe-resubscribe-elements-error'>
        {t('Something went wrong loading the payment form — please reload and try again')}
      </Alert>
    );
  }

  const disabled = submitting || !checkout!.canConfirm;

  return (
    <form onSubmit={handleSubmit} data-testid='stripe-resubscribe-form'>
      <PaymentElement
        options={{
          fields: {
            billingDetails: {
              address: { country: 'never' },
            },
          },
        }}
      />
      <Button
        type='submit'
        variant='contained'
        color='primary'
        disabled={disabled}
        fullWidth
        sx={{ marginTop: spaces.lg }}
        data-testid='stripe-resubscribe-submit'
      >
        {submitting ? <CircularProgress size={20} /> : t('Pay now')}
      </Button>
    </form>
  );
};

const StripeResubscribeCheckoutPage: React.FC = () => {
  const { t } = useTranslation();
  const { router, match: { location: { query } } } = useRouter();
  const { isLgScreen, isMdScreen } = useScreenSize();
  const isDesktopScreen = isLgScreen || isMdScreen;
  const user = useSelector(selectUser);
  const queryRecord = query as Record<string, string | undefined>;

  const planParam = readQueryParam(queryRecord, 'targetPlanKey');
  const plan = isValidPlan(planParam) ? planParam : null;

  const stripePromise = useMemo<Promise<StripeJs | null>>(
    () => loadStripe(config.stripe.publishableKey),
    [],
  );
  const [interval, setIntervalValue] = useState<CheckoutInterval>('monthly');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<CheckoutErrorKind | null>(null);
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState('');
  // SM-187: Ref-Rabatt (trainee-discount) toggle — surfaced for pro/fullAccess only.
  const [refRabattOn, setRefRabattOn] = useState(false);
  const [promoError, setPromoError] = useState(false);
  const [promoErrorReason, setPromoErrorReason] = useState<PromoErrorReason | null>(null);
  const [applying, setApplying] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const showRefRabattToggle = !!plan && REF_RABATT_PLANS.includes(plan);
  // The Ref-Rabatt toggle wins over a manually-applied code.
  const effectivePromo = (refRabattOn ? REF_RABATT_CODE : appliedPromo).trim();
  const promoForSession = effectivePromo.length > 0 ? effectivePromo : undefined;

  // Cohort gate. Only basis users with a mapped Stripe customer (and not
  // currently trialing) belong here. Anyone else — paid Stripe customers,
  // Zuora-billed users, fresh Basis users without a Stripe record — gets
  // bounced to the dashboard so they can route through their own upgrade
  // flow (or no flow, in the Zuora case).
  useEffect(() => {
    if (!user) return;
    if (!(user.isBasisUser && user.isStripeUser && !user.isInTrial)) {
      router.push(`/${routes.dashboard.root}`);
    }
  }, [user, router]);

  // Bounce to the upgrade list page if no/invalid target plan was supplied.
  useEffect(() => {
    if (!plan) {
      router.push(`/${routes.stripeUpgrade.root}`);
    }
  }, [plan, router]);

  const isEligibleCohort = !!user && user.isBasisUser && user.isStripeUser && !user.isInTrial;

  useEffect(() => {
    if (!plan || !isEligibleCohort) return;
    let cancelled = false;
    setClientSecret(null);
    setErrorCode(null);
    setLoadFailed(false);
    setApplying(true);

    (async () => {
      try {
        const { clientSecret: next } = await service.resubscribeCheckoutSession({
          plan,
          interval,
          promocode: promoForSession,
        });
        if (cancelled) return;
        setClientSecret(next);
        setErrorCode(null);
        // Note: we deliberately do NOT clear `promoError` / `promoErrorReason`
        // here. After a rejected promo we clear `appliedPromo` (below), which
        // re-runs this effect without the promo — that success path would
        // otherwise reset the inline error a heartbeat after we set it.
        // `handleApplyPromo`, `handleRemoveAppliedPromo`, and typing into the
        // input each reset the state when the user makes a fresh attempt.
      } catch (err) {
        if (cancelled) return;
        const kind = mapErrorToKind(err);
        if (kind === 'InvalidPromoCode') {
          // Surface the inline error + drop the bad promo. Clearing
          // `appliedPromo` re-runs this effect with `promoForSession=undefined`
          // (no separate retry fetch needed) — single request, not two.
          setPromoError(true);
          setPromoErrorReason(readPromoErrorReason(err));
          setAppliedPromo('');
          setRefRabattOn(false);
        } else {
          setClientSecret(null);
          setErrorCode(kind);
          setLoadFailed(true);
        }
      } finally {
        if (!cancelled) setApplying(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [plan, interval, promoForSession, isEligibleCohort]);

  const handlePromoInputChange = useCallback((value: string) => {
    setPromoInput(value);
    // User is reacting to the inline error (editing what they typed) — clear
    // the stale message so they're not staring at the previous code's failure
    // while typing a new one.
    setPromoError(false);
    setPromoErrorReason(null);
  }, []);

  const handleApplyPromo = useCallback(() => {
    setPromoError(false);
    setPromoErrorReason(null);
    setAppliedPromo(promoInput.trim());
  }, [promoInput]);

  const appliedPromoLabel = refRabattOn ? REF_RABATT_CODE : appliedPromo.trim();

  const handleRemoveAppliedPromo = useCallback(() => {
    setPromoError(false);
    setPromoErrorReason(null);
    setPromoInput('');
    setAppliedPromo('');
    setRefRabattOn(false);
  }, []);

  const handleRefRabattChange = useCallback((next: boolean) => {
    setPromoError(false);
    setPromoErrorReason(null);
    setRefRabattOn(next);
    // The toggle wins over any manually-entered code — clear it so the two
    // don't fight over `effectivePromo` when the toggle is switched off again.
    if (!next) {
      setPromoInput('');
      setAppliedPromo('');
    }
  }, []);

  // Reason-aware German message that lands under the promo input. The default
  // "code is invalid" copy in `StripeCheckoutSummary` is fine for the unknown
  // bucket, but Stripe-side rejections like "first-time customers only" tell
  // the user something they can act on, so we override per reason.
  const promoErrorMessage =
    promoError && promoErrorReason
      ? t(PROMO_ERROR_MESSAGE_KEYS[promoErrorReason])
      : undefined;

  const handleSubmitError = useCallback((kind: CheckoutErrorKind) => {
    setErrorCode(kind);
  }, []);

  if (!plan) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppNavBar title={t(routes.stripeUpgradeCheckout.documentTitle)} />
      </Box>
    );
  }

  if (loadFailed) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppNavBar title={t(routes.stripeUpgradeCheckout.documentTitle)} />
        <Box sx={{ padding: spaces.xl }}>
          <Alert severity='error' data-testid='stripe-resubscribe-load-error'>
            {t('stripeUpgrade.loadFailed')}
          </Alert>
        </Box>
      </Box>
    );
  }

  const twoColumnContent = (
    <Box
      data-testid='stripe-resubscribe-page'
      sx={{
        display: 'flex',
        flexDirection: isDesktopScreen ? 'row' : 'column',
        flex: 1,
      }}
    >
      <ColumnContainer
        dataTestId='stripe-resubscribe-page-summary'
        bgcolor={palette.background.paperElevation0}
        isLargeScreen={isDesktopScreen}
      >
        <StripeCheckoutSummary
          planLabel={t(PLAN_LABELS[plan])}
          interval={interval}
          onIntervalChange={setIntervalValue}
          promoInput={promoInput}
          onPromoInputChange={handlePromoInputChange}
          onApplyPromo={handleApplyPromo}
          appliedPromoLabel={appliedPromoLabel}
          onRemoveAppliedPromo={handleRemoveAppliedPromo}
          promoError={promoError}
          promoErrorMessage={promoErrorMessage}
          applying={applying}
          refRabattOn={refRabattOn}
          onRefRabattChange={handleRefRabattChange}
          showRefRabattToggle={showRefRabattToggle}
          pricePanel={clientSecret ? <OrderTotalSummary /> : null}
        />
      </ColumnContainer>

      <ColumnContainer
        dataTestId='stripe-resubscribe-page-form'
        bgcolor={palette.background.paperElevation2}
        isLargeScreen={isDesktopScreen}
      >
        {errorCode && (
          <Alert
            severity='error'
            data-testid={`stripe-resubscribe-error-${errorCode}`}
            sx={{ marginBottom: spaces.md }}
          >
            {t(ERROR_LABELS[errorCode])}
          </Alert>
        )}
        {clientSecret ? (
          <Box data-testid='stripe-resubscribe-elements'>
            <CheckoutForm onSubmitError={handleSubmitError} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
            data-testid='stripe-resubscribe-loading'
          >
            <CircularProgress />
          </Box>
        )}
      </ColumnContainer>
    </Box>
  );

  return (
    <Box
      data-testid='stripe-resubscribe-page-container'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <AppNavBar title={t(routes.stripeUpgradeCheckout.documentTitle)} />
      {clientSecret ? (
        <CheckoutElementsProvider
          key={clientSecret}
          stripe={stripePromise}
          options={{
            clientSecret,
            defaultValues: {
              billingAddress: { address: { country: 'DE' } },
            },
          }}
        >
          {twoColumnContent}
        </CheckoutElementsProvider>
      ) : (
        twoColumnContent
      )}
    </Box>
  );
};

export default StripeResubscribeCheckoutPage;
